import { useState } from "react"
import { Check, Image as ImageIcon, Maximize2, RotateCcw } from "lucide-react"

import ImageLightbox from "./ImageLightbox"
import { PanelCard, PanelCardHead, PanelCardBody } from "./PanelCard"
import PanelEmpty from "./PanelEmpty"
import { Avatar } from "../Avatar"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

// Concept 1 -> "Concept A", matching the customer-facing labels and the number
// the approve action sends back (approved_cover_num).
function conceptLabel(num) {
  return `Concept ${String.fromCharCode(64 + num)}`
}

// Design Preview tab (cover orders) — the real cover concepts a designer uploads
// (cover_preview 1-3), rendered for review with approve-per-direction. Until the
// first concept lands it shows the not-ready state. Mirrors order-design-preview.html.
export default function DesignPreviewPanel({ order, onOpenModal }) {
  const designs = order.deliverables?.designs ?? []
  const approvedNum = order.deliverables?.approvedDesign
  const canApprove = order.permissions?.approveDesign
  const canRevise = order.permissions?.requestRevision
  const designer = order.team?.[0]
  // A collaborator's read-only view (AU-44): the empty state keeps its message
  // but drops the control (it leads to the owner's support dialog), and the
  // concepts are shown without the pick — choosing a direction is the owner's.
  const readOnly = Boolean(order.viewer?.readOnly)

  const [selected, setSelected] = useState(approvedNum || designs[0]?.num || 1)
  const [enlarged, setEnlarged] = useState(-1)

  if (!designs.length) {
    return (
      <PanelEmpty
        Icon={ImageIcon}
        title="Design Preview"
        heading="Concepts on the way"
        body="Your designer is preparing your first cover concepts. We'll let you know the moment they land."
        action={
          !readOnly && (
            <Button variant="outline" size="sm" onClick={() => onOpenModal?.("support")}>
              Message your designer
            </Button>
          )
        }
      />
    )
  }

  return (
    <PanelCard>
      <PanelCardHead Icon={ImageIcon} title="Design Preview">
        {designer && (
          <span className="flex items-center gap-2 text-[12.5px] text-ink-3">
            Designed by
            <Avatar initials={designer.initials} name={designer.name} size={22} />
            <b className="font-semibold text-foreground">{designer.name}</b>
          </span>
        )}
      </PanelCardHead>
      <PanelCardBody>
        <div className="mb-5 rounded-xl border border-border bg-accent px-4 py-3 text-[12.5px] leading-[1.55] text-muted-foreground">
          {readOnly
            ? "The cover concepts delivered so far. Approving a direction or requesting changes is up to the project owner."
            : "Pick a direction to approve, or request changes on any concept. Once you approve, your designer prepares print-ready files for paperback and eBook."}
        </div>

        {/* 3 concepts across, 2 below 860, stacked below 560. A lone concept
            sizes to its image, so a wraparound cover gets the panel's width. */}
        <div
          className={cn(
            "grid grid-cols-1 gap-[18px]",
            designs.length >= 3
              ? "min-[561px]:grid-cols-2 min-[861px]:grid-cols-3"
              : designs.length === 2 ? "min-[561px]:grid-cols-2" : "w-fit max-w-full"
          )}
        >
          {designs.map((d, index) => {
            const isSelected = !readOnly && d.num === selected
            const isApproved = approvedNum && d.num === approvedNum
            // The picture opens the larger view; the row under it picks the
            // concept. Read-only, that row is plain text: no pick.
            const Footer = readOnly ? "div" : "button"
            return (
              <div
                key={d.num}
                className={cn(
                  "flex flex-col overflow-hidden rounded-2xl border bg-card text-left transition-colors",
                  isSelected ? "border-2 border-primary shadow-[0_0_0_4px_var(--red-soft)]" : "border-border",
                  !readOnly && !isSelected && "hover:border-line-2"
                )}
              >
                {/* A concept is shown whole, in its own shape: a front cover is
                    portrait, but a wraparound (back, spine, front) is wider than
                    tall and a fixed portrait crop showed only its middle. */}
                <button
                  type="button"
                  aria-label={`Open ${conceptLabel(d.num)} larger`}
                  onClick={() => setEnlarged(index)}
                  className="group relative flex w-full flex-1 cursor-zoom-in items-center bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground"
                >
                  <img
                    src={d.url}
                    alt={conceptLabel(d.num)}
                    className={cn("block h-auto", designs.length === 1 ? "max-h-[480px] w-auto max-w-full" : "w-full")}
                    loading="lazy"
                  />
                  {(isApproved || isSelected) && (
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-[10px] font-bold text-white">
                      <Check className="size-3" strokeWidth={2.4} />
                      {isApproved ? "Approved" : "Your pick"}
                    </div>
                  )}
                  <span className="absolute bottom-3 right-3 grid size-[26px] place-items-center rounded-[7px] border border-border bg-card opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 max-[560px]:opacity-100">
                    <Maximize2 className="size-[14px] text-foreground" strokeWidth={2} />
                  </span>
                </button>
                {/* The three-across band is where this row is starved: the order
                    detail page brings its vertical tab rail in at 861 and the
                    panel column drops from 538px to 305px in that same instant,
                    while the grid above steps 2 -> 3 columns. Measured card
                    widths: 260px at 860, then 90 / 103 / 136 / 144 at
                    861 / 900 / 1000 / 1024. The LABEL survives that — it is a
                    div and wraps ("Concept" / "A"). The pill cannot: "Selected"
                    is one unbreakable word, so at 82px it ran past the card's own
                    `overflow-hidden` edge by 71px at 861 and 16px at 1024 (a real
                    find of 78-muster-gate's clipping probe, invisible until the
                    seed carried actual cover concepts).
                      `flex-wrap` lets the pill take its own line rather than
                    fight the label for one, and `max-w-full` + `truncate` keep it
                    inside the card in the last 90-103px case where even a whole
                    line is not enough. Both are earned: measured with the cap
                    removed the pill still overflows 11px at 861, and with the
                    wrap removed it is mangled at every starved width (56px of
                    "Selected" cut at 861, still 13px at 1112). Nothing changes
                    where the row already fit — at 365/768/1280 the label and pill
                    render whole with identical geometry — and 1000/1024 now show
                    BOTH in full instead of clipping. */}
                <Footer
                  {...(readOnly ? {} : { type: "button", onClick: () => setSelected(d.num) })}
                  className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3.5 text-left"
                >
                  <div className="font-display text-[12.5px] font-bold">{conceptLabel(d.num)}</div>
                  {!readOnly && (
                    <span
                      className={cn(
                        "max-w-full truncate rounded-lg border px-3 py-1.5 text-[11.5px] font-bold",
                        isSelected ? "border-primary bg-primary text-white" : "border-line-2 text-muted-foreground"
                      )}
                    >
                      {isSelected ? "Selected" : "Select"}
                    </span>
                  )}
                </Footer>
              </div>
            )
          })}
        </div>

        <ImageLightbox
          wide
          title="Design Preview"
          images={designs.map((d) => ({ url: d.url, label: conceptLabel(d.num) }))}
          at={enlarged}
          onMove={setEnlarged}
          onClose={() => setEnlarged(-1)}
        />

        {/* This bar bleeds out of the card body, so its negative margins have to
            track PanelCardBody's own padding at every width — otherwise it
            overhangs the card below 720 and pushes the page sideways. */}
        {(canApprove || canRevise) && (
          <div className="-mx-[22px] -mb-5 mt-6 flex flex-wrap items-center gap-4 border-t border-border bg-accent px-[22px] py-4 max-[720px]:-mx-[14px] max-[720px]:-mb-4 max-[720px]:gap-3 max-[720px]:px-4 max-[720px]:py-[14px]">
            <div className="max-[720px]:basis-full">
              <h4 className="font-display text-sm font-bold">{conceptLabel(selected)} selected</h4>
              <p className="mt-0.5 text-[12px] text-ink-3">Approve to lock the direction and request print-ready files.</p>
            </div>
            <div className="ml-auto flex gap-[9px] max-[720px]:w-full">
              {canRevise && (
                <Button variant="outline" className="gap-[7px] max-[720px]:flex-1" onClick={() => onOpenModal?.("request-revision")}>
                  <RotateCcw className="size-4" strokeWidth={2} />
                  Request changes
                </Button>
              )}
              {canApprove && (
                <Button className="gap-[7px] font-bold max-[720px]:flex-1" onClick={() => onOpenModal?.("approve-design", { concept: selected })}>
                  <Check className="size-4" strokeWidth={2.2} />
                  Approve cover
                </Button>
              )}
            </div>
          </div>
        )}
      </PanelCardBody>
    </PanelCard>
  )
}
